import { Injectable } from '@angular/core';
import { CanMatch, Route, UrlSegment } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, Observable, of } from 'rxjs';
import { DeviceConstantsService } from 'src/app/dashboard/devices/device-constants.service';
import { DevicesService } from 'src/app/dashboard/devices/devices.service';
import { Device } from 'src/app/dashboard/devices/device.interface';
import { UsersService } from 'src/app/dashboard/users/users.service';
import { AccessFunctions } from 'src/app/theme/shared/access-functions';

/**
 * Guards `device-page/:stage/:id`, which used to accept anything in either segment.
 *
 * This is a canMatch guard rather than canActivate on purpose: returning false makes the router
 * treat the route as unmatched, so the URL falls through to the catch-all `**` route. The 404 page
 * then renders with the bad URL still in the address bar, and no tab content appears first.
 */
@Injectable({
	providedIn: 'root'
})
export class DeviceRouteGuard implements CanMatch {
	constructor(
		private deviceConstants: DeviceConstantsService,
		private translate: TranslateService,
		private devicesService: DevicesService,
		private usersService: UsersService,
	) { }

	canMatch(route: Route, segments: UrlSegment[]): boolean | Observable<boolean> {
		// segments = ['device-page', <stage>, <imei>, ...<tab>]
		const stageSlug = segments[1]?.path;
		const imei = segments[2]?.path;

		if (!stageSlug || !imei)
			return false;

		if (!this.getStageSlugs().includes(stageSlug))
			return false;

		// RMA/Discard are only openable by Shipment Management (VMSPRO-530). Everyone else falls
		// through to the normal, unwidened lookup below, which the backend excludes those two
		// stages from — so the URL 404s exactly as it did before this stage was reachable at all.
		if (this.getRetiredStageSlugs().includes(stageSlug) && this.usersService.hasAccessFunctions([AccessFunctions.shipment_management]))
			return this.fetchAndCacheDevice(imei);

		return this.devicesService.deviceExistsByImei(imei);
	}

	/**
	 * Fetches with the widened `device-page` search source and seeds the device into
	 * DevicesService's cache, so the tab that mounts next reads it via `getCurrentDevice()` (the
	 * pattern every tab already uses) instead of issuing its own, still-excluded 'search' fetch.
	 */
	private fetchAndCacheDevice(imei: string): Observable<boolean> {
		return this.devicesService.list({ search_by: 'imei', is_partial: false, is_deleted: false, source: 'device-page', keyword: imei }).pipe(
			map((res: { data?: { devices?: Device[] } }) => {
				const device = res?.data?.devices?.[0];
				if (!device)
					return false;

				this.devicesService.setCurrentDevice(device);
				return true;
			}),
			catchError(() => of(true))
		);
	}

	/**
	 * Built from the stage list, not hardcoded, so a new stage does not need a change here.
	 * Same slug shape the devices list page builds (devices.component.ts).
	 */
	private getStageSlugs(): string[] {
		return this.deviceConstants.deviceStageOptions.map((option) =>
			this.translate.instant(option.label).split(' ').join('-')
		);
	}

	private getRetiredStageSlugs(): string[] {
		const retiredStages: number[] = [this.deviceConstants.DeviceStage.RMA, this.deviceConstants.DeviceStage.DISCARD];

		return this.deviceConstants.deviceStageOptions
			.filter((option) => retiredStages.includes(option.value))
			.map((option) => this.translate.instant(option.label).split(' ').join('-'));
	}
}
